import React, { useEffect, useMemo, useState } from 'react';
import TierPageBanner from '../components/portfolio/TierPageBanner';
import {
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Download,
  Mail,
  MessageSquare,
  CheckCircle,
  XCircle,
  Clock,
} from 'lucide-react';
import { apiService } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';
import { sanitizeEmailHtml } from '../utils/sanitizeEmailHtml';

type NotificationType = 'SMS' | 'EMAIL';
type NotificationStatus = 'sent' | 'failed' | 'pending';

interface NotificationAuditLog {
  _id: string;
  userId?: string;
  accountId?: string;
  notificationType: NotificationType;
  recipient: string;
  subject?: string;
  message: string;
  status: NotificationStatus;
  errorMessage?: string;
  twilioMessageId?: string;
  sendgridMessageId?: string;
  metadata?: Record<string, any>;
  sentAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface NotificationAuditStats {
  summary: { total: number };
  byType: Array<{ _id: NotificationType; count: number }>;
  byStatus: Array<{ _id: NotificationStatus; count: number }>;
  groupedByDay: Array<{
    _id: string;
    count: number;
    sent: number;
    failed: number;
    sms: number;
    email: number;
  }>;
}

const NotificationAudits: React.FC = () => {
  const [logs, setLogs] = useState<NotificationAuditLog[]>([]);
  const [stats, setStats] = useState<NotificationAuditStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    page: 1,
    limit: 50,
    notificationType: '' as '' | NotificationType,
    status: '' as '' | NotificationStatus,
    recipient: '',
    notificationCategory: '',
    startDate: '',
    endDate: '',
    sortBy: 'createdAt',
    sortOrder: 'desc' as 'asc' | 'desc',
  });

  const cleanFilters = useMemo(() => {
    const out: any = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== '' && v !== null && v !== undefined) out[k] = v;
    });
    return out;
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [logsData, statsData] = await Promise.all([
        apiService.getNotificationAuditLogs(cleanFilters),
        apiService.getNotificationAuditStats({
          startDate: filters.startDate || undefined,
          endDate: filters.endDate || undefined,
        }),
      ]);

      setLogs(logsData.logs || []);
      setStats(statsData || null);
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          'Failed to load notification audit logs',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.limit, filters.sortBy, filters.sortOrder]);

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const toggleExpand = (id: string) => {
    setExpandedLogs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const formatDate = (s?: string) => (s ? new Date(s).toLocaleString() : '—');

  const getTypeIcon = (t: NotificationType) =>
    t === 'EMAIL' ? (
      <Mail className="h-4 w-4 text-blue-600" />
    ) : (
      <MessageSquare className="h-4 w-4 text-indigo-600" />
    );

  const getStatusIcon = (status: NotificationStatus) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-600" />;
      case 'pending':
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const exportCsv = () => {
    const header = [
      'createdAt',
      'notificationType',
      'status',
      'recipient',
      'subject',
      'twilioMessageId',
      'sendgridMessageId',
      'notificationCategory',
      'errorMessage',
    ];

    const rows = logs.map((l) => [
      l.createdAt || '',
      l.notificationType,
      l.status,
      l.recipient,
      (l.subject || '').replace(/\n/g, ' '),
      l.twilioMessageId || '',
      l.sendgridMessageId || '',
      (l.metadata?.notificationCategory as string) || '',
      (l.errorMessage || '').replace(/\n/g, ' '),
    ]);

    const csv = [header, ...rows]
      .map((r) =>
        r
          .map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`)
          .join(','),
      )
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `notification-audits-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <TierPageBanner
        title="Notifications · tier events"
        description="Tier 1 rebalance and review emails, plus orchestration alerts, surface here when enabled."
      />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Notification Audit Logs
          </h1>
          <p className="text-sm text-gray-600">
            Track SMS/Email sends for debugging and transparency
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters((v) => !v)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-gray-200 bg-white hover:bg-gray-50"
          >
            <Filter className="h-4 w-4" />
            Filters
          </button>
          <button
            onClick={exportCsv}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-gray-200 bg-white hover:bg-gray-50"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
          <button
            onClick={loadData}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600">Total notifications</p>
              <p className="text-2xl font-bold">{stats.summary.total}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600">By type</p>
              <div className="mt-2 space-y-1 text-sm">
                {stats.byType.map((t) => (
                  <div key={t._id} className="flex items-center justify-between">
                    <span className="text-gray-700">{t._id}</span>
                    <span className="font-medium">{t.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-gray-600">By status</p>
              <div className="mt-2 space-y-1 text-sm">
                {stats.byStatus.map((s) => (
                  <div key={s._id} className="flex items-center justify-between">
                    <span className="text-gray-700">{s._id}</span>
                    <span className="font-medium">{s.count}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showFilters && (
        <Card>
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Type
                </label>
                <select
                  value={filters.notificationType}
                  onChange={(e) =>
                    handleFilterChange(
                      'notificationType',
                      e.target.value as any,
                    )
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                >
                  <option value="">All</option>
                  <option value="SMS">SMS</option>
                  <option value="EMAIL">EMAIL</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) =>
                    handleFilterChange('status', e.target.value as any)
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                >
                  <option value="">All</option>
                  <option value="sent">sent</option>
                  <option value="failed">failed</option>
                  <option value="pending">pending</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Recipient contains
                </label>
                <input
                  value={filters.recipient}
                  onChange={(e) =>
                    handleFilterChange('recipient', e.target.value)
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Category
                </label>
                <input
                  value={filters.notificationCategory}
                  onChange={(e) =>
                    handleFilterChange('notificationCategory', e.target.value)
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Start date
                </label>
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) =>
                    handleFilterChange('startDate', e.target.value)
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  End date
                </label>
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) =>
                    handleFilterChange('endDate', e.target.value)
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>
            </div>
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  setFilters((p) => ({
                    ...p,
                    page: 1,
                    notificationType: '',
                    status: '',
                    recipient: '',
                    notificationCategory: '',
                    startDate: '',
                    endDate: '',
                  }));
                  setTimeout(loadData, 0);
                }}
                className="px-3 py-2 text-sm font-medium rounded-md border border-gray-200 bg-white hover:bg-gray-50"
              >
                Reset
              </button>
              <button
                onClick={loadData}
                className="px-3 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700"
              >
                Apply
              </button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Logs</CardTitle>
            <div className="text-sm text-gray-600">
              Showing {logs.length} (page {filters.page})
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10">
              <LoadingSpinner />
            </div>
          ) : logs.length === 0 ? (
            <div className="py-10 text-center text-gray-500">No logs found</div>
          ) : (
            <div className="space-y-3">
              {logs.map((log) => {
                const expanded = expandedLogs.has(log._id);
                const category = (log.metadata?.notificationCategory as string) || '';
                const providerId =
                  log.notificationType === 'SMS'
                    ? log.twilioMessageId
                    : log.sendgridMessageId;

                return (
                  <div
                    key={log._id}
                    className="border border-gray-200 rounded-lg"
                  >
                    <button
                      onClick={() => toggleExpand(log._id)}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-3">
                        {expanded ? (
                          <ChevronDown className="h-4 w-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        )}
                        {getTypeIcon(log.notificationType)}
                        {getStatusIcon(log.status)}
                        <div className="text-left">
                          <div className="text-sm font-medium text-gray-900">
                            {log.recipient}{' '}
                            <span className="text-gray-500">
                              · {log.notificationType}
                              {category ? ` · ${category}` : ''}
                            </span>
                          </div>
                          <div className="text-xs text-gray-600">
                            {formatDate(log.createdAt)}{' '}
                            {providerId ? `· id: ${providerId}` : ''}
                          </div>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500">{log._id}</div>
                    </button>

                    {expanded && (
                      <div className="px-4 pb-4">
                        {log.subject && (
                          <div className="mt-2">
                            <div className="text-xs font-medium text-gray-700">
                              Subject
                            </div>
                            <div className="text-sm text-gray-900">
                              {log.subject}
                            </div>
                          </div>
                        )}
                        <div className="mt-2">
                          <div className="text-xs font-medium text-gray-700">
                            {log.notificationType === 'EMAIL'
                              ? 'Message (preview, images blocked)'
                              : 'Message'}
                          </div>
                          {log.notificationType === 'EMAIL' ? (
                            <div
                              className="mt-1 text-sm text-gray-900 bg-white border border-gray-200 rounded-md p-3 max-h-64 overflow-auto"
                              style={{
                                fontFamily:
                                  'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
                                lineHeight: 1.45,
                              }}
                              // Sanitized before rendering to prevent XSS.
                              dangerouslySetInnerHTML={{
                                __html: sanitizeEmailHtml(log.message),
                              }}
                            />
                          ) : (
                            <pre className="mt-1 whitespace-pre-wrap text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-md p-3 max-h-64 overflow-auto">
                              {log.message}
                            </pre>
                          )}
                        </div>
                        {log.errorMessage && (
                          <div className="mt-2">
                            <div className="text-xs font-medium text-red-700">
                              Error
                            </div>
                            <pre className="mt-1 whitespace-pre-wrap text-sm text-red-900 bg-red-50 border border-red-200 rounded-md p-3">
                              {log.errorMessage}
                            </pre>
                          </div>
                        )}
                        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          <div className="text-gray-700">
                            <span className="font-medium">Sent at:</span>{' '}
                            {formatDate(log.sentAt)}
                          </div>
                          <div className="text-gray-700">
                            <span className="font-medium">Updated at:</span>{' '}
                            {formatDate(log.updatedAt)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-4 flex items-center justify-between">
            <button
              disabled={filters.page <= 1}
              onClick={() => setFilters((p) => ({ ...p, page: p.page - 1 }))}
              className="px-3 py-2 text-sm font-medium rounded-md border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setFilters((p) => ({ ...p, page: p.page + 1 }))}
              className="px-3 py-2 text-sm font-medium rounded-md border border-gray-200 bg-white hover:bg-gray-50"
            >
              Next
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationAudits;


